// eslint-disable no-nested-ternary
import { pdsTv } from '@purinanbm/pds-ui';
import React from 'react';
import { CMS_THEME } from 'src/common/enums';
import ButtonDiadWrapper from '../button/ButtonDiadWrapper';
import RichText from '../rich_text/RichText';

export type TextAlignProp =
  | 'left'
  | 'center'
  | 'right'
  | {
      xs: 'left' | 'center' | 'right';
      sm?: 'left' | 'center' | 'right';
      md?: 'left' | 'center' | 'right';
      lg?: 'left' | 'center' | 'right';
    };

export type ContentBlockProps = {
  title: {
    element: keyof JSX.IntrinsicElements;
    className?: string;
    children: React.ReactNode | string;
  };
  subtitle?: {
    element: keyof JSX.IntrinsicElements;
    className?: string;
    children: React.ReactNode | string;
  };
  body?: {
    children: React.ReactNode | string;
    className?: string;
  };
  textAlign?: TextAlignProp;
  theme?: CMS_THEME;
  children?: React.ReactNode | string;
};

const getResponsiveTextAlignClasses = (textAlign: TextAlignProp) => {
  if (typeof textAlign === 'object') {
    return [
      textAlign.xs
        ? `pds-text-${textAlign.xs} ${textAlign.xs === 'center' ? 'pds-mx-auto' : ''}`
        : '',
      textAlign.sm
        ? `sm:pds-text-${textAlign.sm} ${textAlign.sm === 'center' ? 'sm:pds-mx-auto' : ''}`
        : '',
      textAlign.md
        ? `md:pds-text-${textAlign.md} ${textAlign.md === 'center' ? 'md:pds-mx-auto' : ''}`
        : '',
      textAlign.lg
        ? `lg:pds-text-${textAlign.lg} ${textAlign.lg === 'center' ? 'lg:pds-mx-auto' : ''}`
        : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  const textAlignMap = {
    left: 'pds-text-left',
    center: 'pds-text-center pds-mx-auto',
    right: 'pds-text-right',
  };

  return textAlignMap[textAlign];
};

export const ContentBlock: React.FC<ContentBlockProps> = ({
  title,
  subtitle = {
    element: 'h2',
    className: '',
    children: null,
  },
  body = {
    children: null,
    className: '',
  },
  children: actions = null,
  theme = CMS_THEME.LIGHT,
  textAlign = 'left',
}) => {
  const { element: TitleElement, className: titleClassName, children: titleChildren } = title;
  const {
    element: SubtitleElement = 'h2',
    className: subtitleClassName = '',
    children: subtitleChildren,
  } = subtitle;
  const { children: bodyChildren, className: bodyClassName } = body;

  const textAlignClasses = getResponsiveTextAlignClasses(textAlign);

  const classNames = pdsTv({
    base: '',
    variants: {
      theme: {
        light: 'pds-text-surface-text',
        dark: 'pds-text-neutral-text',
        neutral: 'pds-text-surface-text',
      },
    },
  });

  return (
    <>
      {titleChildren && (
        <TitleElement
          className={classNames({ theme, className: `${titleClassName} ${textAlignClasses}` })}
        >
          {titleChildren}
        </TitleElement>
      )}
      {subtitleChildren && (
        <SubtitleElement
          className={classNames({ theme, className: `${subtitleClassName} ${textAlignClasses}` })}
        >
          {subtitleChildren}
        </SubtitleElement>
      )}
      {React.isValidElement(bodyChildren) && (
        <RichText cmsTheme={theme} className={classNames({ className: textAlignClasses })}>
          {bodyChildren}
        </RichText>
      )}
      {typeof bodyChildren === 'string' && !React.isValidElement(bodyChildren) && (
        <RichText
          body={bodyChildren}
          cmsTheme={theme}
          className={classNames({
            className: `pds-max-w-[800px] ${bodyClassName} ${textAlignClasses}`,
          })}
        />
      )}
      {actions && (
        <ButtonDiadWrapper
          className={classNames({ className: `pds-mt-4.5 md:pds-mt-5.5 ${textAlignClasses}` })}
        >
          {actions}
        </ButtonDiadWrapper>
      )}
    </>
  );
};
